import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { track, trackFailed } from '../../../../../shared-web/track';
import { ApiError } from '../../auth/api-client';
import { apiFailureReason, emberApi, isAuthRequired, type RoomFelt, type RoomLine, type RoomPageResponse } from '../api';
import { Modal } from '../components/Modal';
import { PageFailure } from '../components/PageFailure';
import { PageLoading } from '../components/PageLoading';
import { PresenceAvatar } from '../components/PresenceAvatar';
import { RoomInvitation, moreToOpen } from '../components/RoomInvitation';
import { hourLine } from '../letter-format';
import { useRoomStream } from '../components/room-stream';
import { roomTitle } from '../components/room-title';
import { TalkPageHeader } from './Talk';
import { notifyKnocksChanged } from '../components/use-knocks';
import { useChatsLive, useChatsSnapshot } from '../chats-store';
import { goChats, navigate } from '../router';
import { roomTimer } from '../room-timer';
import { RoomReactionCapsules } from '../components/RoomReactions';
import './RoomPage.css';

const FELT: Array<{ key: RoomFelt; label: string }> = [
  { key: 'say', label: 'I could say what I needed to' },
  { key: 'understood', label: 'I felt understood' },
  { key: 'accepted', label: 'I felt accepted' },
  { key: 'fun', label: 'It was fun' },
  { key: 'none', label: 'Not really any of these' },
];

function when(at: string | null): string {
  if (!at) return '';
  const d = new Date(at);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return time;
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `yesterday ${time}`;
  return `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${time}`;
}

/**
 * A room's page `/rooms/:roomId` (v4.0 §4.21, 2026-09-25 拍板; on the site's
 * paper since 四次拍板 the same day — the thread lives in the talk page, so
 * this page is a letter on the desk, not a night panel): a small flame, the
 * room's name, and one of four states — the invitation, the wait after yes,
 * the closed room, what remains after leaving (one's own lines, to delete).
 * Members only; a 404 to everyone else.
 */
export function RoomPage({ roomId }: { roomId: string }): ReactElement {
  const [page, setPage] = useState<RoomPageResponse | null>(null);
  const [failed, setFailed] = useState<false | 'gone' | 'lapsed' | 'load_failed'>(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [refused, setRefused] = useState('');
  const [leaving, setLeaving] = useState(false);
  const [reason, setReason] = useState(false);
  // Delete / Report ask first (2026-09-30 Riley): the line a tap landed on, awaiting the word
  const [asking, setAsking] = useState<{ line: RoomLine; what: 'delete' | 'report' } | null>(null);
  const [felt, setFelt] = useState<RoomFelt[]>([]);
  const feedRef = useRef<HTMLDivElement | null>(null);
  const seenRef = useRef(false);
  const shownRef = useRef(false);   // a page is on screen: a re-read that fails leaves it there

  const load = (): void => {
    emberApi.room(roomId)
      .then((r) => {
        // Two new friends' room still waiting on their yeses is their introduction, not a room page (2026-10-26, New friends).
        if (r.room.kind === 'pair' && r.room.status === 'forming') { navigate(`/friends/intro/${encodeURIComponent(roomId)}`, { replace: true }); return; }
        // A seated member of an open room reads it inside the talk page
        // (§4.21, 2026-09-25 二次拍板); this page keeps the invitation, the
        // wait, the close and what remains after leaving.
        if (r.me.joined && !r.me.left && !r.me.removed && r.room.status === 'open') { navigate(`/talk?mode=chat&room=${encodeURIComponent(roomId)}`, { replace: true }); return; }
        setPage(r);
        shownRef.current = true;
        if (!seenRef.current) {
          seenRef.current = true;
          track('ui.clicked', { name: 'room_open', state: r.me.left ? 'left' : (r.me.joined ? r.room.status : 'invited'), match_id: roomId });
          if (r.me.joined && !r.me.left && !r.me.removed) emberApi.roomSeen(roomId).then(() => notifyKnocksChanged()).catch(() => { /* next time */ });
          setReason(!r.lines.some((l) => l.kind === 'human'));
        }
      })
      .catch((err: unknown) => {
        // Nobody signed in — a texted or pushed room link opened in another
        // browser, or a session that ran out (2026-10-04): the sign-in door,
        // and this page again after it. Before, every non-404 read as
        // "Couldn't open this. Try again in a moment."
        if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent(`/rooms/${roomId}`)}`, { replace: true }); return; }
        const gone = err instanceof ApiError && err.status === 404;
        if (!gone) trackFailed('room_load', 'load_failed', { error_detail: apiFailureReason(err) });
        // A re-read in the background (after the look, on coming back to the tab) that fails leaves the page as it was
        // (2026-10-27 walk-through: the database blinked and a chat being read turned into "Couldn't open this").
        if (!gone && shownRef.current) return;
        // A room that never came together says so (2026-10-18; edge room_lapsed, still a 404).
        const lapsed = gone && (err.body as { error?: string } | null)?.error === 'room_lapsed';
        setFailed(lapsed ? 'lapsed' : gone ? 'gone' : 'load_failed');
      });
  };
  useEffect(() => { seenRef.current = false; shownRef.current = false; setFailed(false); }, [roomId]);
  // The hour set, the room opening, its close, another's answer, a join, a leave: the App hears by push and re-reads; here
  // the page reads as it opens, again on every read of their Chats (2026-10-31 founder: a page follows the one change
  // signal — the live channel's announcements, and a letter moving, which reads the Chats too), and when the tab comes
  // back. No polling on the web (2026-09-30 Riley 拍板, the same word as for the thread).
  useChatsLive(true);
  const chats = useChatsSnapshot().data;
  useEffect(() => { load(); }, [roomId, chats]);
  useEffect(() => {
    const again = (): void => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', again);
    return () => document.removeEventListener('visibilitychange', again);
  }, [roomId]);
  // A seated member of an open room is sent to the talk page above; should
  // this page still hold one (the redirect in flight), the thread is live
  // here too (2026-09-29; room-stream.ts), and the close reloads the page.
  useRoomStream(roomId, Boolean(page && page.me.joined && !page.me.left && !page.me.removed && page.room.status === 'open'), setPage, () => load());
  useEffect(() => { feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight }); }, [page?.lines.length]);

  // A room's two ends keep the room's header (2026-10-22 jam): back is to the Chats list, as on every other state of
  // this page and in the apps — the page's own Back to Home and Home beside it were two doors to the same place.
  if (failed === 'lapsed') return <><TalkPageHeader to="/chats" /><PageFailure toolbar={false} title="This room didn’t come together." detail="Not enough people said yes." logState="lapsed" /></>;
  if (failed === 'gone') return <><TalkPageHeader to="/chats" /><PageFailure toolbar={false} title="This room isn’t here." detail="It may have closed to you, or the link is old." logState="not_found" /></>;
  if (failed) return <PageFailure title="Couldn’t open this." detail="Try again in a moment." logState="load_failed" />;
  if (!page) return <PageLoading />;

  const { room, me, members } = page;

  const send = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true); setRefused('');
    try {
      const out = await emberApi.sayInRoom(roomId, text);
      track('ui.clicked', { name: 'room_line_send', match_id: roomId });
      setPage((cur) => (cur ? { ...cur, lines: [...cur.lines, out.line] } : cur));
      setDraft('');
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      const body = err instanceof ApiError ? (err.body as { error?: string; stage?: string; message?: string } | null) : null;
      trackFailed('room_line_send', body?.error === 'text_rejected' ? 'text_rejected' : (body?.error || 'error'));
      setRefused(body?.error === 'text_rejected'
        ? (body.message || 'That line can’t go into the room as written. Say it another way.')
        : body?.error === 'muted' ? 'Ember has asked you to sit this day out.'
          : body?.error === 'text_too_long' ? `That’s more than one line here holds (${page.room.line_max_chars} characters). Send it in two.`
            : status === 409 ? 'The room isn’t taking lines right now.' : 'Couldn’t send that. Try again.');
    } finally { setSending(false); }
  };

  const remove = async (line: RoomLine): Promise<void> => {
    try { await emberApi.deleteRoomLine(roomId, line.id); track('ui.clicked', { name: 'room_line_delete', match_id: roomId }); setPage((cur) => (cur ? { ...cur, lines: cur.lines.filter((l) => l.id !== line.id) } : cur)); } catch { trackFailed('room_line_send', 'delete_failed'); }
  };
  const report = async (line: RoomLine): Promise<void> => {
    try { await emberApi.reportRoomLine(roomId, line.id); track('ui.clicked', { name: 'room_line_report', match_id: roomId }); setPage((cur) => (cur ? { ...cur, lines: cur.lines.filter((l) => l.id !== line.id) } : cur)); } catch { trackFailed('room_line_send', 'report_failed'); }
  };
  const leave = async (takeLines: boolean): Promise<void> => {
    try { await emberApi.leaveRoom(roomId, takeLines); track('ui.clicked', { name: 'room_leave_confirm', state: takeLines ? 'taken' : 'kept', match_id: roomId }); notifyKnocksChanged(); navigate('/chats'); } catch { trackFailed('room_leave', 'error'); setLeaving(false); }
  };
  const saveFelt = async (): Promise<void> => {
    if (!felt.length) return;
    try { await emberApi.roomClosingFelt(roomId, felt); track('ui.clicked', { name: 'room_closing_felt', state: felt.join(','), match_id: roomId }); setPage({ ...page, me: { ...page.me, felt } }); } catch { trackFailed('room_closing_felt', 'error'); }
  };

  // The invitation: the page is the letter while the answer is open (redrawn
  // 2026-09-25 四次拍板: on the site's paper, one flame, the room's name, the
  // one sentence about why, the seat one would take, one button).
  if (!me.joined && !me.left && me.answer !== 'yes') {
    return (
      <><TalkPageHeader to="/chats" />
      <main className="room-page room-invite">
        <div className="room-hearth"><span className="room-flame" aria-hidden /></div>
        <p className="room-kicker">A room, if you want it</p>
        <h1 className="room-title" dir="auto">{roomTitle(room.name)}</h1>
        <p className="room-reason" dir="auto">{room.fork}</p>
        <RoomInvitation
          roomId={roomId}
          me={me}
          nameMaxChars={room.name_max_chars}
          onRenamed={(name) => setPage((cur) => (cur ? { ...cur, me: { ...cur.me, name } } : cur))}
          open={room.status === 'open' ? { people: page.people, since: room.opened_at } : null}
          yeses={room.yeses ?? []}
          opensOnYeses={room.opens_on_yeses}
          opensAt={room.opens_at}
          onAnswered={(answer, out) => {
            // A no leaves the way Back would: to the Chats list (2026-10-20).
            if (answer === 'no') { goChats(); return; }
            if (out.opened || out.joined) navigate(`/talk?mode=chat&room=${encodeURIComponent(roomId)}`, { replace: true });
            else setPage({ ...page, room: { ...page.room, opens_at: out.opens_at ?? page.room.opens_at }, me: { ...page.me, answer: 'yes' } });
          }}
        />
      </main></>
    );
  }

  // The wait after yes (2026-10-02 拍板, the owner): the room's door before
  // it opens, not a receipt. The seats already taken stand on it — the others
  // who said yes and this person's own, with the name they carry here — so the
  // page is the room as it stands (the Rooms shelf's card lands here, and here
  // reads as the room). There is nowhere further in until the room opens, when
  // this page sends them straight into the thread; the header's back is the way
  // back, to the Chats list with Ember at its top (2026-10-22 jam: the big
  // "Back to chat" went to Ember's conversation, which is not where they came
  // from — the apps took it off on 2026-10-20).
  // The room's name is the heading, where the invitation had it (2026-10-16
  // 拍板): as a small capital kicker over "You said yes." it read as a line
  // about the person — "UNSURE WHERE YOU STAND / You said yes." — so the yes
  // opens the sentence under it.
  if (me.answer === 'yes' && !me.joined && !me.left) {
    const others = room.yeses ?? [];
    // A topic room waits for people, not for an hour (2026-10-26 founder): it opens with the third in, and is listed on
    // the New friends page rather than in the chats. Rooms by situation keep their words.
    const topic = room.kind === 'topic';
    return (
      <><TalkPageHeader to="/chats" />
      <main className="room-page room-invite">
        <div className="room-hearth"><span className="room-flame" aria-hidden /></div>
        <ul className="room-seats" aria-label="Who has said yes" data-log-seen="room_wait_door" data-log-state={String(others.length)} data-log-match-id={roomId}>
          {others.map((y) => (
            <li key={y.name} className="room-seat">
              <span className="room-seat-btn">
                <PresenceAvatar avatarKey={y.avatar} size={46} />
                <span className="room-seat-name" dir="auto">{y.name}</span>
              </span>
            </li>
          ))}
          <li className="room-seat is-me">
            <span className="room-seat-btn">
              <PresenceAvatar avatarKey={me.avatar} size={46} />
              <span className="room-seat-name" dir="auto">{me.name}</span>
            </span>
          </li>
        </ul>
        <h1 className="room-title" dir="auto">{roomTitle(room.name)}</h1>
        {topic
          ? <p className="room-wait-sub">Waiting for people · {others.length + 1} of {room.opens_on_yeses} in · it opens when {room.opens_on_yeses} are in</p>
          : <p className="room-wait-sub">You said yes. {room.opens_at ? `The room opens ${hourLine(room.opens_at)}. Ember will let you know when it opens.` : `${moreToOpen(room.opens_on_yeses, others.length)} Ember will let you know.`}</p>}
        <p className="room-foot">They’ll know you as {me.name} and nothing more. {topic ? 'Ember will let you know when it opens.' : 'This door stays in your chats.'}</p>
      </main></>
    );
  }

  const closed = room.status === 'closed';
  // How long the room has (需求一, room-timer.ts); the last day in the accent colour.
  const timer = roomTimer(room);
  // Someone who left, or whom Ember closed the room to, sees only what they
  // said here and can delete it (design §5.4).
  const gone = me.left || me.removed;
  return (
    <><TalkPageHeader to="/chats" />
    <main className="room-page">
      <div className="room-hearth"><span className="room-flame" aria-hidden /></div>
      {!gone && (
        <ul className="room-seats" aria-label="Who is here">
          {members.map((m) => (
            <li key={m.name} className={`room-seat${m.me ? ' is-me' : ''}`}>
              <span className="room-seat-btn">
                <PresenceAvatar avatarKey={m.avatar} size={46} />
                <span className="room-seat-name">{m.me ? 'you' : m.name}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {/* Gone with their lines, nothing of theirs is here to head the page (2026-10-27 walk-through: "What you said here" over
          "You took your lines with you"); and the room's state is the room's, not their words'. */}
      <h1 className="room-title" dir="auto">{gone && !me.took_lines ? 'What you said here' : roomTitle(room.name)}</h1>
      <p className="room-meta">
        {gone ? (closed ? 'closed' : 'the room goes on for the others') : closed ? `closed · stays readable to ${page.people === 1 ? 'you' : `the ${page.people} of you`}` : <>{page.people === 1 ? 'you' : `${page.people} of you`} and Ember · {timer ? <span className={timer.urgent ? 'room-meta-urgent' : undefined}>{timer.text}</span> : 'open a while longer'}</>}
        {/* A topic room was put up by its people, not brought together by Ember: no "why" (2026-10-26). */}
        {!gone && room.kind !== 'topic' && <> · <button type="button" className="room-link" data-log="room_reason_open" data-log-match-id={roomId} aria-expanded={reason} onClick={() => setReason(!reason)}>why Ember brought you together ›</button></>}
      </p>
      {reason && !gone && room.kind !== 'topic' && <p className="room-reason" dir="auto">{room.fork}</p>}

      <div className="room-lines" ref={feedRef}>
        {page.lines.map((l) => (
          l.kind === 'system'
            ? <p key={l.id} className="room-sys">{l.text}</p>
            : (
              <div key={l.id} className={`room-line is-${l.kind}${l.mine ? ' is-mine' : ''}${!l.mine && l.seq > me.read_seq ? ' is-new' : ''}`}>
                <div className="room-line-head"><b>{l.kind === 'host' ? 'Ember' : (l.mine ? 'you' : l.author)}</b> <span>{when(l.at)}</span></div>
                <div className="room-line-body" dir="auto">{l.text}</div>
                {/* The emoji the line carries (需求四), read-only here: this page is for a room past its open days, or one left. */}
                {l.reactions && l.reactions.length > 0 && <RoomReactionCapsules reactions={l.reactions} roomId={roomId} align="start" />}
                {l.kind === 'human' && !closed && (
                  <div className="room-line-acts">
                    {l.mine
                      ? <button type="button" className="room-link" data-log="room_line_delete_open" data-log-match-id={roomId} onClick={() => setAsking({ line: l, what: 'delete' })}>Delete</button>
                      : <button type="button" className="room-link" data-log="room_line_report_open" data-log-match-id={roomId} onClick={() => setAsking({ line: l, what: 'report' })}>Report</button>}
                  </div>
                )}
              </div>
            )
        ))}
        {/* Took their lines: the foot says so once (2026-10-27 walk-through: said twice, a line apart). */}
        {!page.lines.length && !(gone && me.took_lines) && <p className="room-sys">{gone ? 'None of your lines remain here.' : 'Nothing said yet.'}</p>}
      </div>

      {!closed && !gone && (
        <form className="room-composer" onSubmit={(e) => { void send(e); }}>
          <textarea
            id="room-composer-field"
            className="room-field"
            value={draft}
            maxLength={600}
            rows={1}
            placeholder={me.muted_until && new Date(me.muted_until) > new Date() ? 'Ember has asked you to sit this day out' : 'Say something to the room'}
            disabled={sending || Boolean(me.muted_until && new Date(me.muted_until) > new Date())}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(e); } }}
            aria-label="Say something to the room"
          />
          <button type="submit" className="room-send" disabled={sending || !draft.trim()} aria-label="Send">↑</button>
        </form>
      )}
      {refused && <p className="room-refused" role="alert">{refused}</p>}

      {/* A room's question; a chat between two friends had its own week's check-in (2026-10-27 walk-through). */}
      {closed && !gone && !me.felt && room.kind !== 'pair' && (
        <section className="room-felt" aria-labelledby="room-felt-head">
          <p id="room-felt-head" className="room-felt-head">How was it, being here?</p>
          <div className="room-chips">
            {FELT.map((f) => (
              <button key={f.key} type="button" className={`room-chip${felt.includes(f.key) ? ' is-on' : ''}`} aria-pressed={felt.includes(f.key)}
                onClick={() => setFelt((cur) => (f.key === 'none' ? ['none'] : (cur.includes(f.key) ? cur.filter((k) => k !== f.key) : [...cur.filter((k) => k !== 'none'), f.key])))}>
                {f.label}
              </button>
            ))}
          </div>
          <button type="button" className="room-btn primary" disabled={!felt.length} onClick={() => { void saveFelt(); }}>That’s how it was</button>
        </section>
      )}
      {/* Ember's closing word already says the page stays (2026-10-27 walk-through: "The page stays." twice in a row). */}
      {closed && !gone && me.felt && room.kind !== 'pair' && <p className="room-sys">Thank you.</p>}

      <p className="room-foot">
        {/* "Yours to delete" only while the room is open, where Delete is (2026-10-27 walk-through: it stood under a closed room with no Delete). */}
        {me.removed ? `Ember closed this room to you.${closed ? '' : ' Your lines are yours to delete.'}` : me.left ? (room.kind === 'pair' ? (me.took_lines ? 'You left this chat and took your lines with you.' : 'You left this chat.') : me.took_lines ? 'You left this room and took your lines with you.' : `You left this room.${closed ? '' : ' Your lines are yours to delete.'}`) : room.kind === 'pair' ? (closed ? 'This chat has wrapped up. That happens, and it’s okay — what you two said stays here for you to read.' : 'Only the two of you and Ember can read this.') : 'Only the few of you and Ember can read this.'}
        {!closed && !gone && !leaving && <> · <button type="button" className="room-link" data-log="room_leave_open" data-log-match-id={roomId} onClick={() => setLeaving(true)}>Leave this room</button></>}
      </p>
      {leaving && (
        <div className="room-leave" role="group" aria-label="Leave this room">
          {room.kind !== 'pair' && <p className="room-sys">You won’t be able to come back in.</p>}
          <button type="button" className="room-btn" onClick={() => { void leave(false); }}>Leave, keep my lines</button>
          <button type="button" className="room-btn" onClick={() => { void leave(true); }}>Leave, take my lines</button>
          <button type="button" className="room-link" onClick={() => setLeaving(false)}>Cancel</button>
        </div>
      )}
      {asking && (
        <Modal onClose={() => setAsking(null)} labelledBy="room-ask-title" className="settings-bind-modal">
          <h2 id="room-ask-title" className="settings-bind-title">{asking.what === 'delete' ? 'Delete this line?' : 'Report this line?'}</h2>
          <p className="settings-bind-body">{asking.what === 'delete' ? 'It’s gone for everyone in the room. This can’t be undone.' : 'Ember’s team will look at this line and what it’s answering.'}</p>
          <div className="settings-bind-actions">
            <button type="button" className="ember-btn ember-btn-ghost" onClick={() => setAsking(null)}>Keep it</button>
            <button type="button" className="ember-btn ember-btn-primary" onClick={() => { const a = asking; setAsking(null); void (a.what === 'delete' ? remove(a.line) : report(a.line)); }}>{asking.what === 'delete' ? 'Delete' : 'Report'}</button>
          </div>
        </Modal>
      )}
    </main></>
  );
}
